import { useEffect, useState } from 'react';
import { Cloud, CloudOff, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useCloudSyncStore } from '@/state/cloudSyncStore';
import { useAuthStore } from '@/state/authStore';
import { syncNow } from '@/state/cloudSync';

/**
 * Phase 16.4.4 — Sync-Status-Indicator (oben rechts neben TopBar).
 *
 * Zeigt mit Icon + Tooltip den aktuellen Sync-State. Klick öffnet ein kleines
 * Popover mit Detail-Text + manuellem „Jetzt synchronisieren"-Button.
 */
export function SyncIndicator() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const status = useCloudSyncStore((s) => s.status);
  const lastSyncedAt = useCloudSyncStore((s) => s.lastSyncedAt);
  const lastError = useCloudSyncStore((s) => s.lastError);
  const [open, setOpen] = useState(false);

  // Auto-relative-time refresh
  const [, force] = useState(0);
  useEffect(() => {
    const id = setInterval(() => force((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  if (!isAuthenticated) return null;

  const Icon = pickIcon(status);
  const tone = pickTone(status);

  return (
    <div className="pointer-events-auto fixed right-4 top-12 z-30">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={pickTitle(status, lastSyncedAt)}
        aria-label="Sync-Status"
        data-testid="sync-indicator"
        className={[
          'flex h-8 w-8 items-center justify-center rounded-full border border-white/5 shadow-lg backdrop-blur-md transition',
          tone,
        ].join(' ')}
        style={{ background: 'rgba(20, 24, 36, 0.9)' }}
      >
        <Icon
          className={[
            'h-3.5 w-3.5',
            status === 'syncing' ? 'animate-spin' : '',
          ].join(' ')}
          aria-hidden
        />
      </button>

      {open && (
        <div
          role="dialog"
          className="absolute right-0 top-10 w-64 overflow-hidden rounded-md border border-border-strong bg-bg-secondary text-xs shadow-2xl"
        >
          <div className="border-b border-border-subtle px-4 py-2.5">
            <p className="font-display text-sm font-semibold">Cloud-Sync</p>
            <p className="mt-0.5 text-fg-muted">{pickTitle(status, lastSyncedAt)}</p>
            {lastError && (
              <p className="mt-1 text-[10px] text-accent-emergency">Fehler: {lastError}</p>
            )}
          </div>
          <button
            type="button"
            onClick={async () => {
              setOpen(false);
              await syncNow();
            }}
            className="flex w-full items-center gap-2 px-4 py-2.5 text-left transition hover:bg-bg-elevated"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden />
            Jetzt synchronisieren
          </button>
        </div>
      )}
    </div>
  );
}

function pickIcon(status: string): LucideIcon {
  if (status === 'synced') return CheckCircle2;
  if (status === 'syncing') return RefreshCw;
  if (status === 'offline') return CloudOff;
  if (status === 'error') return AlertCircle;
  return Cloud;
}

function pickTone(status: string): string {
  if (status === 'synced') return 'text-status-available';
  if (status === 'syncing') return 'text-accent-dispatch';
  if (status === 'offline') return 'text-amber-400';
  if (status === 'error') return 'text-accent-emergency';
  return 'text-fg-muted';
}

function pickTitle(status: string, lastSyncedAt: number | null): string {
  if (status === 'syncing') return 'Wird synchronisiert …';
  if (status === 'offline') return 'Offline — wird sich bei Verbindung erneut versuchen';
  if (status === 'error') return 'Sync-Fehler';
  if (status === 'synced' && lastSyncedAt) {
    return `Synced ${formatRel(lastSyncedAt)}`;
  }
  return 'Noch nicht synchronisiert';
}

function formatRel(ts: number): string {
  const sec = Math.floor((Date.now() - ts) / 1000);
  if (sec < 30) return 'gerade eben';
  if (sec < 90) return 'vor 1 Min';
  if (sec < 3600) return `vor ${Math.floor(sec / 60)} Min`;
  if (sec < 86400) return `vor ${Math.floor(sec / 3600)} h`;
  return `vor ${Math.floor(sec / 86400)} d`;
}
